import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { APIService } from 'app/API.service.custom';

/** "₹" for INR; the code itself when the browser has no narrow symbol for it. */
export function currencySymbol(currency: string | null | undefined): string {
  const code = currency || 'INR';
  try {
    const part = new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? code;
  } catch {
    return code + ' ';
  }
}

/** Header warning: 'stopped' when the wallet is empty, 'low' below the threshold. */
export type WalletAlert = 'none' | 'low' | 'stopped';

@Injectable({ providedIn: 'root' })
export class WalletBalanceService {
  // v1: a single absolute threshold in the wallet currency. Make this configurable
  // when alert preferences land — keep wallet page + chip in lock-step until then.
  readonly LOW_BALANCE_THRESHOLD_INR = 500;

  /** Money value of the balance, in major units (rupees), from `amountMinor`. */
  private balance$ = new BehaviorSubject<number | null>(null);
  private currency$ = new BehaviorSubject<string>('INR');
  /** Whether the account has a wallet record at all; no record means nothing to warn about. */
  private hasWallet$ = new BehaviorSubject<boolean>(false);
  private lastUpdatedAt$ = new BehaviorSubject<string | null>(null);
  private loading$ = new BehaviorSubject<boolean>(false);
  private error$ = new BehaviorSubject<boolean>(false);

  readonly balance: Observable<number | null> = this.balance$.asObservable();
  readonly currency: Observable<string> = this.currency$.asObservable();
  readonly lastUpdatedAt: Observable<string | null> = this.lastUpdatedAt$.asObservable();
  readonly loading: Observable<boolean> = this.loading$.asObservable();
  /** True when the last refresh failed. A failed load is "unavailable", never ₹0. */
  readonly error: Observable<boolean> = this.error$.asObservable();

  constructor(private api: APIService) {}

  get currentBalance(): number | null {
    return this.balance$.value;
  }

  get isLoading(): boolean {
    return this.loading$.value;
  }

  isLow(balance: number | null): boolean {
    return balance != null && balance < this.LOW_BALANCE_THRESHOLD_INR;
  }

  /** Warning for the header chip, from the balance alone (no capability state is loaded there). */
  get alert(): WalletAlert {
    const balance = this.balance$.value;
    if (!this.hasWallet$.value || balance == null) return 'none';
    if (balance <= 0) return 'stopped';
    return this.isLow(balance) ? 'low' : 'none';
  }

  clear(): void {
    this.balance$.next(null);
    this.currency$.next('INR');
    this.hasWallet$.next(false);
    this.lastUpdatedAt$.next(null);
    this.loading$.next(false);
    this.error$.next(false);
  }

  async refresh(accountId: string): Promise<number> {
    if (this.loading$.value) {
      return this.balance$.value ?? 0;
    }
    this.loading$.next(true);
    try {
      // accountId is the client-side workspace id, which the backend stores
      // as `clientAccountId` on the wallet record.
      const result = await this.api.ListWalletsByClientAccount(
        accountId,
        undefined,
        undefined,
        undefined,
        20,
      );
      const wallets = (result?.items ?? []).filter(
        (wallet): wallet is NonNullable<typeof wallet> => !!wallet,
      );
      // One wallet per account; prefer the INR one if several exist.
      // balanceMinor is in tokens; the money value is amountMinor (minor units of `currency`).
      const wallet = wallets.find((w) => w.currency === 'INR') ?? wallets[0];
      const balanceInr = wallet ? (wallet.amountMinor ?? 0) / 100 : 0;
      this.currency$.next(wallet?.currency || 'INR');
      this.hasWallet$.next(!!wallet);
      this.balance$.next(balanceInr);
      this.lastUpdatedAt$.next(new Date().toISOString());
      this.error$.next(false);
      return balanceInr;
    } catch (error) {
      this.error$.next(true);
      throw error;
    } finally {
      this.loading$.next(false);
    }
  }
}
